// src/components/POS/cart/CartSidebar.js
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { ArrowLeft, Hash, Trash2, X } from "lucide-react";
import { useCart } from "../../contexts/cartContext";
import { usePOSProductsContext } from "../../contexts/POSProductContext";
import { ShoppingCart } from "lucide-react";
import { useTheme } from "../../../CommonComponents/ThemeProvider";
import CartItemRow from "./CartItemRow";
import EditCartItemModal from "../modals/EditCartItemModal";
import PaymentPanel from "../payment/PaymentPanel";
import CashPaymentPanel from "../payment/CashPaymentPanel";
import PaymentSummary from "../payment/PaymentSummary";
import OrderSummaryModal from "../payment/OrderSummaryModal";
import OrderSuccessModal from "../payment/OrderSuccessModal";
import OrderFailedModal from "../payment/OrderFailedModal";
import ConfirmationModal from "../../../CommonComponents/ConfirmationModal";
import EmptyState from "../../../CommonComponents/EmptyState";
import { formatPrice } from "../../utils/FormatPrice";
import { createGcashPaymentRequest, createOrder, fetchHitpayPaymentMethods } from "../../../../services/orderApi";
import { getPaymentMethodLabel, isHitpayEwalletMethod } from "../../../../constant/paymentMethod";
import { printReceipt } from "../../../../utils/receiptPrinter";
import { showErrorToast, showSuccessToast } from "../../../../utils/toast";

const PENDING_HITPAY_PAYMENT_KEY = "pending_hitpay_payment";

const buildOrderFingerprint = ({ cart, paymentMethod, amountPaid, seatNumber, total, referenceLast4 = "" }) => {
    const user = (() => {
        try {
            return JSON.parse(localStorage.getItem("user"));
        } catch (error) {
            return null;
        }
    })();
    const cartSignature = [...cart]
        .map((item) => `${item.productId || item.id}:${item.qty}:${Number(item.price || 0).toFixed(2)}`)
        .sort()
        .join("|");
    const windowBucket = Math.floor(Date.now() / 60000);
    return [
        "pos",
        user?.id || "user",
        user?.branch_id || "branch",
        cartSignature,
        paymentMethod || "payment",
        String(referenceLast4 || "").trim(),
        Number(amountPaid || 0).toFixed(2),
        String(seatNumber || "").trim().toLowerCase(),
        Number(total || 0).toFixed(2),
        windowBucket,
    ].join("|");
};

export default function CartSidebar({ onCloseSidebar }) {
    // Collapsible state for medium screens
    const [collapsed, setCollapsed] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState("cash");
    const [paymentConfiguration, setPaymentConfiguration] = useState({
        enabled: [],
        configured: [],
        mode: "sandbox",
        collectionMode: "live",
    });
    const [manualReferenceLast4, setManualReferenceLast4] = useState("");
    const { cart, removeFromCart, clearCart, restoreCart, cartCountHighlight } = useCart();
    const { currentTheme } = useTheme();
    const { updateStockAfterOrder } = usePOSProductsContext();
    const handlePaymentMethodChange = useCallback((nextMethod) => {
        setPaymentMethod(nextMethod);
        setManualReferenceLast4("");
    }, []);

    const [editItem, setEditItem] = useState(null);
    const [deleteItem, setDeleteItem] = useState(null);
    const [isClearCartOpen, setIsClearCartOpen] = useState(false);
    const [checkoutStep, setCheckoutStep] = useState("review");
    const [isOrderSummaryOpen, setIsOrderSummaryOpen] = useState(false);
    const [isOrderSuccessOpen, setIsOrderSuccessOpen] = useState(false);
    const [isPaymentErrorOpen, setIsPaymentErrorOpen] = useState(false);
    const [paymentErrorMessage, setPaymentErrorMessage] = useState("");
    const [pendingPayment, setPendingPayment] = useState(null);
    const [lastConfirmedOrder, setLastConfirmedOrder] = useState(null);
    const [lastAddedItem, setLastAddedItem] = useState(null);
    const [seatNumber, setSeatNumber] = useState("");
    const [isConfirmingOrder, setIsConfirmingOrder] = useState(false);
    const orderSubmissionRef = useRef({ inFlight: false, key: null });
    const pendingOrderListRef = useRef(null);

    const subtotal = useMemo(
        () => cart.reduce((sum, item) => sum + item.price * item.qty, 0),
        [cart]
    );
    const total = subtotal;
    const totalQty = useMemo(
        () => cart.reduce((sum, item) => sum + Number(item.qty || 0), 0),
        [cart]
    );
    const uniqueItemCount = useMemo(
        () => new Set(cart.map((item) => String(item.name || item.productId || "").trim().toLowerCase()).filter(Boolean)).size,
        [cart]
    );
    const pendingOrderSignature = useMemo(
        () => cart.map((item) => `${item.lineId || item.id || item.productId}:${item.qty}`).join("|"),
        [cart]
    );

    useEffect(() => {
        if (cart.length === 0 && checkoutStep !== "review") {
            setCheckoutStep("review");
        }
    }, [cart.length, checkoutStep]);

    useEffect(() => {
        if (checkoutStep !== "review" || cart.length === 0) return;
        const list = pendingOrderListRef.current;
        if (!list) return;

        const frame = window.requestAnimationFrame(() => {
            const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
            list.scrollTo({
                top: list.scrollHeight,
                behavior: prefersReducedMotion ? "auto" : "smooth",
            });
        });

        return () => window.cancelAnimationFrame(frame);
    }, [pendingOrderSignature, checkoutStep, cart.length]);

    useEffect(() => {
        let cancelled = false;
        const loadPaymentMethods = async () => {
            try {
                const response = await fetchHitpayPaymentMethods();
                if (cancelled) return;
                const methods = Array.isArray(response?.methods)
                    ? response.methods.map((method) => method.key || method.value || method).filter(Boolean)
                    : [];
                const configured = Array.isArray(response?.configured_methods)
                    ? response.configured_methods.map((method) => method.key || method.value || method).filter(Boolean)
                    : methods;
                setPaymentConfiguration({
                    enabled: methods,
                    configured,
                    mode: response?.mode || "sandbox",
                    collectionMode: response?.collection_mode || "live",
                });
                setPaymentMethod((current) => (
                    isHitpayEwalletMethod(current) && !methods.includes(current) ? "cash" : current
                ));
            } catch (error) {
                console.error("Failed to load e-wallet methods:", error);
            }
        };
        loadPaymentMethods();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("payment_provider") !== "hitpay") return;

        const returnedStatus = String(params.get("status") || params.get("payment_status") || "").toLowerCase();
        const paidStatuses = new Set(["paid", "completed", "succeeded", "success"]);
        const shouldRestoreCart = !paidStatuses.has(returnedStatus);

        if (!shouldRestoreCart) {
            sessionStorage.removeItem(PENDING_HITPAY_PAYMENT_KEY);
            return;
        }

        try {
            const pendingHitpayPayment = JSON.parse(sessionStorage.getItem(PENDING_HITPAY_PAYMENT_KEY) || "null");
            if (Array.isArray(pendingHitpayPayment?.cart) && pendingHitpayPayment.cart.length > 0) {
                restoreCart(pendingHitpayPayment.cart);
                setPaymentMethod(pendingHitpayPayment.payment_method || "cash");
                setSeatNumber(pendingHitpayPayment.seat_number || "");
                setCheckoutStep("checkout");
                showErrorToast("Online payment was not completed. Cart restored so you can choose another payment method.");
            }
        } catch (error) {
            console.error("Failed to restore pending HitPay cart:", error);
        } finally {
            sessionStorage.removeItem(PENDING_HITPAY_PAYMENT_KEY);
            const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
            window.history.replaceState({}, document.title, cleanUrl);
        }
    }, [restoreCart]);

    const handlePlaceOrder = useCallback(
        ({ paymentMethod, amountPaid, referenceLast4 = "" }) => {
            if (cart.length === 0) return;
            const isManualOnline = paymentConfiguration.collectionMode === "manual" && isHitpayEwalletMethod(paymentMethod);
            const normalizedReferenceLast4 = String(referenceLast4 || "").replace(/\D/g, "").slice(-4);
            if (isManualOnline && normalizedReferenceLast4.length !== 4) {
                setPaymentErrorMessage("Enter the last 4 digits from the payment reference before completing this manual online payment.");
                setIsPaymentErrorOpen(true);
                return;
            }
            const orderKey = buildOrderFingerprint({
                cart,
                paymentMethod,
                amountPaid,
                seatNumber,
                total,
                referenceLast4: normalizedReferenceLast4,
            });
            orderSubmissionRef.current = { inFlight: false, key: orderKey };
            setPendingPayment({
                cart,
                subtotal,
                total,
                paymentMethod,
                cashReceived: amountPaid,
                payment_collection_mode: paymentMethod === "cash"
                    ? "CASH"
                    : isManualOnline
                        ? "MANUAL_ONLINE"
                        : "LIVE_ONLINE",
                payment_reference_last4: isManualOnline ? normalizedReferenceLast4 : null,
                seat_number: seatNumber.trim() || null,
                idempotency_key: orderKey,
            });
            setIsOrderSummaryOpen(true);
        },
        [cart, subtotal, total, seatNumber, paymentConfiguration.collectionMode]
    );

    const confirmOrder = useCallback(async () => {
        if (isConfirmingOrder || !pendingPayment || orderSubmissionRef.current.inFlight) return;
        const idempotencyKey = pendingPayment.idempotency_key || orderSubmissionRef.current.key || `pos-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        orderSubmissionRef.current = { inFlight: true, key: idempotencyKey };
        setIsConfirmingOrder(true);
        try {
            const user = JSON.parse(localStorage.getItem("user"));
            if (!user?.id) throw new Error("User not logged in");

            const mappedCart = pendingPayment.cart.map(item => ({
                product_id: item.productId, // always use productId for consistency
                qty: item.qty,
                price: item.price,
            }));

            const orderData = {
                user_id: user.id,
                cart: mappedCart,
                payment_method: pendingPayment.paymentMethod,
                payment_collection_mode: pendingPayment.payment_collection_mode,
                payment_reference_last4: pendingPayment.payment_reference_last4,
                seat_number: pendingPayment.seat_number,
                amount_received: pendingPayment.paymentMethod === "cash"
                    ? pendingPayment.cashReceived
                    : null,
                idempotency_key: idempotencyKey,
            };

            if (isHitpayEwalletMethod(pendingPayment.paymentMethod) && pendingPayment.payment_collection_mode !== "MANUAL_ONLINE") {
                const paymentRequest = await createGcashPaymentRequest({
                    ...orderData,
                    discount: 0,
                });

                if (!paymentRequest?.checkout_url) {
                    throw new Error(`HitPay did not return a ${getPaymentMethodLabel(pendingPayment.paymentMethod)} checkout URL.`);
                }

                sessionStorage.setItem(PENDING_HITPAY_PAYMENT_KEY, JSON.stringify({
                    payment_request_id: paymentRequest.payment_request_id,
                    idempotency_key: idempotencyKey,
                    payment_method: pendingPayment.paymentMethod,
                    cart: pendingPayment.cart,
                    seat_number: pendingPayment.seat_number,
                    subtotal: pendingPayment.subtotal,
                    total: pendingPayment.total,
                    created_at: new Date().toISOString(),
                }));
                setIsOrderSummaryOpen(false);
                setPendingPayment(null);
                clearCart();
                setSeatNumber("");
                setCheckoutStep("review");
                window.location.assign(paymentRequest.checkout_url);
                return;
            }

            const createdOrder = await createOrder(orderData);
            const printableOrder = {
                ...createdOrder,
                cart: pendingPayment.cart,
                subtotal: pendingPayment.subtotal,
                total: pendingPayment.total,
                payment_method: pendingPayment.paymentMethod,
                payment_collection_mode: pendingPayment.payment_collection_mode,
                payment_reference_last4: pendingPayment.payment_reference_last4,
                amount_received: pendingPayment.paymentMethod === "cash" ? pendingPayment.cashReceived : null,
                change_amount: pendingPayment.paymentMethod === "cash"
                    ? Math.max(Number(pendingPayment.cashReceived || 0) - Number(pendingPayment.total || 0), 0)
                    : null,
                seat_number: pendingPayment.seat_number,
            };
            try {
                await printReceipt(printableOrder);
            } catch (printError) {
                showErrorToast(printError.response?.data?.message || "Sale completed, but receipt printing failed.");
            }
            setLastConfirmedOrder(printableOrder);
            updateStockAfterOrder(mappedCart);
            window.dispatchEvent(new CustomEvent("pos-order-created", { detail: printableOrder }));
            clearCart();
            setSeatNumber("");
            setManualReferenceLast4("");
            setCheckoutStep("review");
            setIsOrderSummaryOpen(false);
            setIsOrderSuccessOpen(true);
            setPendingPayment(null);
            setLastAddedItem(null);
            orderSubmissionRef.current = { inFlight: false, key: null };
        } catch (error) {
            console.error("Order API error:", error);
            const apiMessage = error.response?.data?.message;
            setPaymentErrorMessage(apiMessage || "Failed to create order. Please try again.");
            setIsOrderSummaryOpen(false);
            setIsPaymentErrorOpen(true);
            orderSubmissionRef.current = { inFlight: false, key: idempotencyKey };
        } finally {
            setIsConfirmingOrder(false);
        }
    }, [isConfirmingOrder, pendingPayment, clearCart, updateStockAfterOrder]);

    // Handler to close the order success modal and reset lastConfirmedOrder
    const handleOrderSuccessClose = () => {
        setIsOrderSuccessOpen(false);
        setLastConfirmedOrder(null);
        if (typeof window !== "undefined" && window.innerWidth < 768 && typeof onCloseSidebar === "function") {
            onCloseSidebar();
        }
    };

    return (
        <div className="flex h-full min-h-0 flex-col">
            <aside
                className="mb-2 flex min-h-[520px] flex-1 flex-col overflow-hidden rounded-xl bg-white shadow-md"
                style={{ maxHeight: '90vh' }}
            >
                <div
                    className="px-4 py-2 flex-shrink-0 flex justify-between items-center border-b border-gray-200"
                >
                    <div>
                        <h2 className="text-[15px] font-semibold text-gray-700 tracking-normal">
                            {checkoutStep === "checkout"
                                ? "Checkout"
                                : cart.length > 0
                                    ? "Pending Order"
                                    : lastConfirmedOrder
                                        ? `Order #${lastConfirmedOrder.order_number}`
                                        : "No Pending Order"}
                        </h2>
                        <p
                            className="text-xs mt-0.5 uppercase tracking-wide text-gray-500"
                        >
                            {cart.length > 0
                                ? `${uniqueItemCount} unique ${uniqueItemCount === 1 ? "item" : "items"} | ${totalQty} total qty`
                                : "Ordered Items"}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {checkoutStep === "checkout" && (
                            <button
                                type="button"
                                onClick={() => setCheckoutStep("review")}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:bg-slate-50"
                                title="Back to order review"
                                aria-label="Back to order review"
                            >
                                <ArrowLeft size={16} />
                            </button>
                        )}
                        {checkoutStep === "review" && cart.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setIsClearCartOpen(true)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-red-100 bg-red-50 text-red-600 shadow-sm transition hover:bg-red-100"
                                title="Clear all items"
                                aria-label="Clear all items"
                            >
                                <Trash2 size={15} />
                            </button>
                        )}
                        {/* Close/collapse button always beside cart counter, visible on all screens */}
                        {/* Show close button only on small screens (floating sidebar) or when collapsed on md+ */}
                        {((typeof window !== 'undefined' && window.innerWidth < 768) || collapsed) && (
                            <button
                                className="inline-flex p-1 rounded hover:bg-gray-100 focus:outline-none"
                                onClick={() => {
                                    if (typeof onCloseSidebar === 'function') {
                                        onCloseSidebar();
                                    } else {
                                        setCollapsed((c) => !c);
                                    }
                                }}
                                aria-label={collapsed ? 'Expand cart sidebar' : 'Collapse cart sidebar'}
                                type="button"
                            >
                                <X size={22} />
                            </button>
                        )}
                        <span className="relative inline-block">
                            <ShoppingCart
                                size={24}
                                className={
                                    (currentTheme?.background === 'dark' || (currentTheme?.color && ['#000', '#222', '#333', '#1a1a1a', '#111'].includes(currentTheme.color.toLowerCase())))
                                        ? 'text-white'
                                        : 'text-black'
                                }
                            />
                            {cart.length > 0 && (
                                <span
                                    className={`absolute -top-2 -right-2 md:-top-2 md:-right-2 text-xs font-bold rounded-full px-2 py-0.5 transition-transform text-white ${cartCountHighlight ? "scale-125" : "scale-100"}`}
                                    style={{ backgroundColor: currentTheme?.color || 'var(--color-primary)' }}
                                >
                                    {cart.reduce((sum, i) => sum + i.qty, 0)}
                                </span>
                            )}
                        </span>
                    </div>
                </div>

                {checkoutStep === "review" ? (
                    <>
                        <div
                            ref={pendingOrderListRef}
                            className="flex-1 overflow-y-auto px-4 py-2 space-y-1"
                            style={{ minHeight: "240px" }}
                        >
                            <div>
                                {cart.length > 0 ? (
                                    cart.map((item, index) => (
                                        <CartItemRow
                                            key={item.lineId || item.id}
                                            item={item}
                                            rowNumber={index + 1}
                                            formatPrice={formatPrice}
                                            onEdit={() => setEditItem(item)}
                                            onDelete={() => setDeleteItem(item)}
                                            highlightKey={lastAddedItem}
                                        />
                                    ))
                                ) : (
                                    <EmptyState icon={ShoppingCart} message="Cart is empty" />
                                )}
                            </div>
                        </div>
                        <div className="flex-shrink-0 space-y-3 rounded-b-xl border-t border-gray-200 bg-white px-4 py-3">
                            <PaymentSummary subtotal={subtotal} total={total} />
                            <button
                                type="button"
                                disabled={cart.length === 0}
                                onClick={() => setCheckoutStep("checkout")}
                                className="flex h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-white"
                                style={cart.length > 0 ? { background: 'var(--color-primary)' } : undefined}
                            >
                                Proceed to Payment
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex-1 space-y-3 overflow-y-auto overflow-x-hidden px-4 py-3">
                            <div
                                className="rounded-2xl p-4 text-white shadow-[0_16px_38px_rgba(15,23,42,0.14)]"
                                style={{
                                    background: "linear-gradient(135deg, var(--color-primary), color-mix(in srgb, var(--color-primary) 78%, black))",
                                }}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ color: "rgba(255,255,255,0.72)" }}>Amount Due</p>
                                        <p className="mt-1 text-2xl font-bold leading-none" style={{ color: "#fff" }}>{formatPrice(total)}</p>
                                    </div>
                                    <span className="rounded-full bg-white/15 px-2.5 py-1 text-[11px] font-semibold" style={{ color: "rgba(255,255,255,0.88)" }}>
                                        {totalQty} qty
                                    </span>
                                </div>
                                <div className="mt-4 rounded-xl bg-white/12 px-3 py-2">
                                    <div className="flex items-center justify-between text-xs">
                                        <span style={{ color: "rgba(255,255,255,0.78)" }}>Subtotal</span>
                                        <span className="font-semibold" style={{ color: "#fff" }}>{formatPrice(subtotal)}</span>
                                    </div>
                                    <div className="mt-1 flex items-center justify-between text-xs">
                                        <span style={{ color: "rgba(255,255,255,0.68)" }}>Adjustments</span>
                                        <span style={{ color: "rgba(255,255,255,0.78)" }}>None</span>
                                    </div>
                                </div>
                            </div>
                            <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                                <label htmlFor="seat-number" className="block text-[12px] font-semibold text-gray-700 mb-1">
                                    Seat Number (Optional)
                                </label>
                                <input
                                    id="seat-number"
                                    type="text"
                                    value={seatNumber}
                                    onChange={(e) => setSeatNumber(e.target.value)}
                                    placeholder="e.g. 12, A3, Table 5"
                                    maxLength={20}
                                    className="w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-700 outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                                />
                            </div>
                            <div>
                                <PaymentPanel
                                    paymentMethod={paymentMethod}
                                    setPaymentMethod={handlePaymentMethodChange}
                                    configuredPaymentMethods={paymentConfiguration.configured}
                                    enabledPaymentMethods={paymentConfiguration.enabled}
                                    paymentMode={paymentConfiguration.mode}
                                    collectionMode={paymentConfiguration.collectionMode}
                                />
                            </div>
                            {paymentMethod === "cash" && (
                                <div>
                                    <CashPaymentPanel total={total} cart={cart} onPay={handlePlaceOrder} />
                                </div>
                            )}
                            {isHitpayEwalletMethod(paymentMethod) && paymentConfiguration.collectionMode === "live" && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                                    <div className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
                                        Customer will complete the {getPaymentMethodLabel(paymentMethod)} payment through HitPay checkout.
                                    </div>
                                    <button
                                        type="button"
                                        disabled={cart.length === 0}
                                        onClick={() => handlePlaceOrder({ paymentMethod, amountPaid: null })}
                                        className="flex h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:bg-slate-300"
                                        style={cart.length > 0 ? { background: 'var(--color-primary)' } : undefined}
                                    >
                                        Review {getPaymentMethodLabel(paymentMethod)} Payment
                                    </button>
                                </div>
                            )}
                            {isHitpayEwalletMethod(paymentMethod) && paymentConfiguration.collectionMode === "manual" && (
                                <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                                    <div className="mb-3 rounded-xl bg-slate-50 px-3 py-2 text-sm leading-5 text-slate-600">
                                        Record the customer's {getPaymentMethodLabel(paymentMethod)} payment after checking their receipt. This will mark the sale as paid in the POS.
                                    </div>
                                    <label className="block">
                                        <span className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                                            <Hash size={13} />
                                            Last 4 reference digits
                                        </span>
                                        <input
                                            value={manualReferenceLast4}
                                            onChange={(event) => setManualReferenceLast4(event.target.value.replace(/\D/g, "").slice(0, 4))}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            maxLength={4}
                                            placeholder={paymentMethod === "card" ? "Card last 4" : "Receipt ref last 4"}
                                            className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-[var(--color-primary)] focus:ring-4 focus:ring-slate-200"
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        disabled={cart.length === 0 || manualReferenceLast4.length !== 4}
                                        onClick={() => handlePlaceOrder({ paymentMethod, amountPaid: null, referenceLast4: manualReferenceLast4 })}
                                        className="mt-3 flex h-11 w-full items-center justify-center rounded-xl px-4 text-sm font-semibold text-white shadow-sm transition hover:brightness-95 disabled:cursor-not-allowed disabled:bg-slate-300"
                                        style={cart.length > 0 && manualReferenceLast4.length === 4 ? { background: 'var(--color-primary)' } : undefined}
                                    >
                                        Review Manual {getPaymentMethodLabel(paymentMethod)} Payment
                                    </button>
                                </div>
                            )}
                        </div>
                    </>
                )}

                {editItem && <EditCartItemModal item={editItem} onClose={() => setEditItem(null)} autoFocus={false} />}
                {deleteItem && (
                    <ConfirmationModal
                        isOpen={!!deleteItem}
                        onClose={() => setDeleteItem(null)}
                        onConfirm={() => {
                            removeFromCart(deleteItem.lineId || deleteItem.id, true);
                            setDeleteItem(null);
                        }}
                        title="Remove Item"
                        message={`Are you sure you want to remove "${deleteItem.name}" from the cart?`}
                        confirmText="Yes, Remove"
                        cancelText="Cancel"
                        type="delete"
                    />
                )}
                {isClearCartOpen && (
                    <ConfirmationModal
                        isOpen={isClearCartOpen}
                        onClose={() => setIsClearCartOpen(false)}
                        onConfirm={() => {
                            clearCart();
                            setSeatNumber("");
                            setManualReferenceLast4("");
                            setCheckoutStep("review");
                            setIsClearCartOpen(false);
                        }}
                        title="Clear Cart"
                        message={`Remove all ${totalQty} ${totalQty === 1 ? "item" : "items"} from this pending order?`}
                        confirmText="Clear All"
                        cancelText="Cancel"
                        type="delete"
                    />
                )}

                {isOrderSummaryOpen && pendingPayment && (
                    <OrderSummaryModal
                        isOpen={isOrderSummaryOpen}
                        onClose={() => {
                            if (!isConfirmingOrder) setIsOrderSummaryOpen(false);
                        }}
                        order={{ ...pendingPayment }}
                        onConfirm={confirmOrder}
                        confirming={isConfirmingOrder}
                    />
                )}


                {isOrderSuccessOpen && lastConfirmedOrder && (
                    <OrderSuccessModal
                        isOpen={isOrderSuccessOpen}
                        onClose={handleOrderSuccessClose}
                        orderNumber={lastConfirmedOrder.order_number}
                        onReprint={async () => {
                            try {
                                await printReceipt(lastConfirmedOrder);
                                showSuccessToast("Receipt sent to printer.");
                            } catch (error) {
                                showErrorToast(error.response?.data?.message || "Receipt printing failed.");
                            }
                        }}
                    />
                )}

                {isPaymentErrorOpen && (
                    <OrderFailedModal
                        isOpen={isPaymentErrorOpen}
                        onClose={() => {
                            setIsPaymentErrorOpen(false);
                            setPaymentErrorMessage("");
                        }}
                        message={paymentErrorMessage || "Failed to create order. Please try again."}
                    />
                )}
            </aside>
        </div>
    );
}
